import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Link as MuiLink,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { CheckCircle, Security } from '@mui/icons-material';
import { useAuth } from './AuthContext';
import config from '../../conf/config';

const ConfirmSignUp = ({ onStateChange, username }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    code: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const { confirmSignUp } = useAuth();

  const validateForm = () => {
    const errors = {};
    
    if (!formData.code) {
      errors.code = 'Please enter the verification code';
    } else if (formData.code.length !== 6) {
      errors.code = 'Verification code must be 6 digits';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await confirmSignUp(username, formData.code);
      setSuccess(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            py: 3
          }}
        >
          <Card sx={{ width: '100%', maxWidth: 400 }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" component="h1" gutterBottom color="success.main" fontWeight="bold">
                Account Verified!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your account has been successfully verified. You can now sign in.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => onStateChange('signIn')}
                sx={{ height: 48, fontSize: '1rem' }}
                fullWidth
              >
                Sign In
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          py: 3
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h4" component="h1" gutterBottom color="primary" fontWeight="bold">
                Verify Your Email
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We've sent a verification code to <strong>{username}</strong>
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="medium">
                  Verification Failed
                </Typography>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Verification Code"
                value={formData.code}
                onChange={handleChange('code')}
                error={!!formErrors.code}
                helperText={formErrors.code}
                inputProps={{ maxLength: 6 }}
                sx={{ 
                  mb: 3,
                  '& .MuiInputBase-input': {
                    textAlign: 'center',
                    letterSpacing: '0.5em'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Security color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ 
                  height: 48,
                  fontSize: '1rem',
                  mb: 2,
                  position: 'relative'
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Verify Account'
                )}
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              {config.features.SIGNUP_ENABLED && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Didn't receive the code?{' '}
                  <MuiLink
                    component="button"
                    variant="body2"
                    onClick={() => onStateChange('signUp')}
                    sx={{ textDecoration: 'none', fontWeight: 500 }}
                  >
                    Try signing up again
                  </MuiLink>
                </Typography>
              )}
              <Typography variant="body2">
                <MuiLink
                  component="button"
                  variant="body2"
                  onClick={() => onStateChange('signIn')}
                  sx={{ textDecoration: 'none', fontWeight: 500 }}
                >
                  Back to sign in
                </MuiLink>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ConfirmSignUp;